import type { KaiEntityTag } from "./kaiEntityTags";

/**
 * A `/` slash command the user selected in the composer for a single message.
 * Carries the display `label` (for the inline pill) alongside the `id` sent to
 * the server and any free-text `args` typed after the pill.
 */
export interface KaiSlashSelection {
  id: string;
  label: string;
  args?: string;
}

export type KaiPlanStepStatus =
  | "pending"
  | "running"
  | "waiting"
  | "ok"
  | "error"
  | "skipped"
  | "cancelled";

export interface KaiPlanStep {
  id: string;
  label: string;
  status?: KaiPlanStepStatus;
}

export interface KaiPlanArtifact {
  title?: string;
  steps: KaiPlanStep[];
}

export interface KaiPlanExecuteMetadata {
  plan?: KaiPlanArtifact;
}

export interface KaiMessageMetadata {
  plan_execute?: KaiPlanExecuteMetadata;
  /** IDs of files attached to this message, in upload order. */
  attachment_ids?: string[];
  /** Filenames paired positionally with `attachment_ids`. */
  filenames?: string[];
}

export type KaiLiveToolStatus = "running" | "done" | "error";

export interface KaiToolActivity {
  name: string;
  status: KaiLiveToolStatus;
  args?: string;
  errorMessage?: string;
  /** Raw tool-result JSON string, used to extract download chips. */
  result?: string;
}

export type KaiMessageRole = "user" | "assistant" | "tool";

export interface KaiMessage {
  id: string;
  role: KaiMessageRole;
  content: string;
  createdAt: string;
  toolName?: string;
  plan?: KaiPlanArtifact | null;
  /** Entities the user explicitly tagged when sending this message. */
  entityTags?: KaiEntityTag[];
  /** Slash command the user invoked when sending this message. */
  slashCommand?: { id: string; label: string };
  /** Free-form metadata (attachment IDs, filenames, etc.). */
  metadata?: KaiMessageMetadata | null;
}

export interface KaiConversation {
  id: string;
  title: string;
  preview: string;
  createdAt: string;
  updatedAt: string;
  messages: KaiMessage[];
}

export type KaiThreadItem =
  | { kind: "message"; message: KaiMessage }
  | { kind: "tool_group"; tools: KaiToolActivity[] }
  | { kind: "plan"; spec: KaiPlanArtifact };

export type ClarificationFieldType =
  | "radio_tags"
  | "text"
  | "date"
  | "port_select"
  | "booking_filter_select";

export type ClarificationFilterKey =
  | "sales_person"
  | "agent"
  | "shipper"
  | "consignee"
  | "opened_by";

export interface ClarificationField {
  id: string;
  label: string;
  type: ClarificationFieldType;
  options?: { value: string; label: string }[];
  filter_key?: ClarificationFilterKey;
  placeholder?: string;
  required?: boolean;
  conditional?: { field: string; value: string };
}

export interface PendingClarification {
  clarificationId: string;
  title: string;
  fields: ClarificationField[];
  status: "pending" | "submitting" | "resolved";
  answers?: Record<string, string>;
}

/**
 * A pending MCP elicitation request (e.g. rate-card creation confirmation)
 * shown inline in the chat. The agent's tool call blocks server-side until the
 * user approves or rejects, so it must be surfaced wherever Kai streams.
 */
export interface PendingElicitation {
  elicitationId: string;
  message: string;
  schema: Record<string, unknown>;
  status: "pending" | "submitting" | "resolved" | "error";
  /** Outcome, set after the user responds. */
  result?: "approved" | "rejected";
  /** MCP tool error surfaced after approval (e.g. API validation failure). */
  errorMessage?: string;
}

// ── Kai structured artifacts ───────────────────────────────────────────────
// Mirror of the serde types in tideway-kai/src/orchestrator/artifacts. Emitted
// as fenced JSON blocks (rate_comparison / action_proposal / guidance) or
// pushed via the `artifact` SSE event.

export interface KaiRateNotice {
  type: string;
  title: string;
  delta_usd?: number;
  effective_date?: string;
  baseline_usd?: number;
  adjusted_usd?: number;
}

export interface KaiWeightBasis {
  cargo_kg?: number | null;
  ows_tier?: string;
  ows_usd?: number;
  gross_kg?: number;
  weight_basis_label?: string;
}

export interface KaiPolicyHints {
  booking?: string;
  dnd?: string;
}

export interface KaiRateSurchargeLine {
  code: string;
  amount_usd: number;
  /** Normalized lowercase basis: `container` | `bl` | `teu` | `shipment` | `ton` | `cbm` | fallback. */
  basis: string;
  /** `ocean` | `local` | `inclusive` when known; absent → "Other" bucket. */
  category?: string;
}

export interface KaiRateRow {
  rate_line_id?: string;
  rate_card_id?: string;
  contract_ref?: string;
  carrier?: string;
  origin_port?: string;
  destination_port?: string;
  equipment_size?: string;
  equipment_type?: string;
  frt_usd: number;
  surcharges_usd?: number;
  all_in_usd: number;
  frt_pp_usd?: number;
  baseline_frt_pp_usd?: number;
  validity_status?: string;
  valid_from?: string;
  valid_to?: string;
  baseline_frt_usd?: number;
  baseline_all_in_usd?: number;
  notices?: KaiRateNotice[];
  weight_basis?: KaiWeightBasis;
  policy_hints?: KaiPolicyHints;
  surcharges?: KaiRateSurchargeLine[];
  surcharge_codes?: string[];
}

export interface KaiRateFamily {
  id: string;
  label: string;
  source_type: "prepaid" | "collect" | "spot";
  agent?: string;
  carrier?: string;
  rows: KaiRateRow[];
  open_in_rates_url?: string;
}

export interface KaiRateComparison {
  title?: string;
  mode?: "comparison" | "lookup" | "best_price";
  families: KaiRateFamily[];
  footnotes?: string[];
  disclaimer?: string;
  origin_port?: string;
  destination_port?: string;
  equipment_size?: string;
  equipment_type?: string;
  carrier?: string;
  valid_on?: string;
  cargo_kg?: number;
}

export interface KaiProposalEntity {
  type: string;
  id: string;
  booking_id?: string;
}

export interface KaiProposalSummary {
  current_all_in_usd?: number;
  proposed_all_in_usd?: number;
  savings_usd?: number;
}

export interface KaiProposalAction {
  label: string;
  url: string;
}

export interface KaiActionProposal {
  title: string;
  kind: string;
  entity?: KaiProposalEntity;
  summary?: KaiProposalSummary;
  primary_action?: KaiProposalAction;
  secondary_actions?: KaiProposalAction[];
  requires_elicitation?: boolean;
  elicitation_tool?: string;
}

export interface GuidanceStep {
  text: string;
  /** Optional internal app route to navigate to (must start with "/"). */
  link?: string;
}

export interface GuidanceSpec {
  title?: string;
  steps: GuidanceStep[];
  /** Id of a tutorial (ignored in v2 — no tutorial system). */
  tutorial_id?: string;
  /** One-line offer for Kai to perform the task itself, sent as a follow-up. */
  practical_offer?: string;
}

// ── Elicitation preview payloads ───────────────────────────────────────────

export interface KaiRateCardDiffLine {
  idx: string;
  route: string;
  equipment: string;
  frt: string;
  surcharges: string;
  all_in: string;
  free_days?: number | null;
}

export interface KaiRateCardOwnership {
  type: "Prepaid" | "Collect" | "Other";
  agent?: string;
  label: string;
}

export interface KaiRateCardPolicy {
  label: string;
  value: string;
}

export interface KaiRateCardSurchargeDerivation {
  source: string;
  label: string;
  carrier: string;
  agent?: string | null;
  codes_applied: string[];
  lines_enriched: number;
  note: string;
}

export interface KaiRateCardOverweightPolicy {
  scope: string;
  equipment: string;
  tiers: string[];
}

export interface KaiRateCardOverweightDerivation {
  source: string;
  label: string;
  carrier: string;
  agent?: string | null;
  policy_count: number;
  replaced_document_policy: boolean;
  policies_summary: string[];
  note: string;
}

export interface KaiRateCardDiff {
  header: { label: string; value: string }[];
  lines: KaiRateCardDiffLine[];
  diff: { added: number; changed?: number };
  ownership?: KaiRateCardOwnership;
  is_spot?: boolean;
  policies?: KaiRateCardPolicy[];
  overweightPolicies?: KaiRateCardOverweightPolicy[];
  warnings?: string[];
  surchargeDerivation?: KaiRateCardSurchargeDerivation;
  overweightDerivation?: KaiRateCardOverweightDerivation;
  freeDaysSummary?: string | null;
  line_count?: number;
}

export type RestrictionProposalAction =
  | "create"
  | "update"
  | "delete"
  | "enable"
  | "disable";

export interface KaiRestrictionProposalPreview {
  action: RestrictionProposalAction;
  rule_type?: string;
  rule_type_label?: string;
  name?: string | null;
  description?: string | null;
  enabled?: boolean | null;
  priority?: number | null;
  severity_override?: string | null;
  params?: Record<string, unknown> | null;
  target_rule_id?: string | null;
  current?: {
    name?: string | null;
    rule_type?: string | null;
    enabled?: boolean | null;
    priority?: number | null;
    severity_override?: string | null;
    description?: string | null;
    params?: Record<string, unknown> | null;
  } | null;
  rationale?: string | null;
}

export type CustomerRequestCategory =
  | "bug"
  | "data_issue"
  | "feature_request"
  | "configuration"
  | "billing"
  | "account"
  | "other";

export interface KaiCustomerRequestPreview {
  category?: CustomerRequestCategory | string | null;
  title?: string | null;
  user_message?: string | null;
  kai_summary?: string | null;
}

export interface KaiSsePayload {
  content?: string;
  delta?: string;
  name?: string;
  arguments?: string;
  result?: string;
  message?: string;
  logger?: string;
  level?: string;
  suggestions?: string[];
  plan?: KaiPlanArtifact;
  step_id?: string;
  step_label?: string;
  tool?: string;
  status?: string;
  preview?: string;
  kind?: string;
  payload?: unknown;
  preferences?: Record<string, unknown>;
  page?: string;
  /**
   * `apply_table_filters`: PropertyFilter patch for the target table.
   * `apply_feed_filters`: virtual-scope patch for the Activity Feed.
   */
  filters?: unknown;
  /** `apply_home_layout_patch`: incremental Home layout change. */
  patch?: unknown;
  /** `home_layout_review_request`: end-of-turn approve prompt. */
  summary?: string;
  widget_count?: number;
  clarification_id?: string;
  title?: string;
  fields?: unknown;
  elicitation_id?: string;
  schema?: Record<string, unknown>;
}

/** Compact summary of tool arguments from a JSON string. */
export function formatToolArgs(
  argsJson: string | undefined | null,
): string | undefined {
  if (!argsJson) return undefined;
  try {
    const obj = JSON.parse(argsJson) as Record<string, unknown>;
    if (typeof obj !== "object" || obj === null) return undefined;
    const parts: string[] = [];
    for (const [key, val] of Object.entries(obj)) {
      if (val === null || val === undefined) continue;
      if (typeof val === "string") {
        parts.push(
          `${key}: "${val.length > 30 ? `${val.slice(0, 30)}...` : val}"`,
        );
      } else if (typeof val === "number" || typeof val === "boolean") {
        parts.push(`${key}: ${val}`);
      }
    }
    if (parts.length === 0) return undefined;
    const joined = parts.join(", ");
    return joined.length > 80 ? `${joined.slice(0, 77)}...` : joined;
  } catch {
    return undefined;
  }
}

export function updatePlanStep(
  plan: KaiPlanArtifact,
  stepId: string,
  status: KaiPlanStepStatus,
): KaiPlanArtifact {
  return {
    ...plan,
    steps: plan.steps.map((s) => (s.id === stepId ? { ...s, status } : s)),
  };
}

export function parsePlanSpec(json: string): KaiPlanArtifact | null {
  try {
    const parsed = JSON.parse(json) as KaiPlanArtifact;
    if (!parsed || typeof parsed !== "object") return null;
    if (!Array.isArray(parsed.steps) || parsed.steps.length === 0) return null;
    for (const step of parsed.steps) {
      if (typeof step?.id !== "string" || !step.id) return null;
      if (typeof step?.label !== "string" || !step.label) return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function groupKaiThread(messages: KaiMessage[]): KaiThreadItem[] {
  const result: KaiThreadItem[] = [];
  let toolBuffer: KaiMessage[] = [];

  const flushTools = () => {
    if (toolBuffer.length === 0) return;
    result.push({
      kind: "tool_group",
      tools: toolBuffer.map((t) => {
        const isErr = t.content.startsWith("Error:");
        return {
          name: t.toolName || "unknown",
          status: isErr ? ("error" as const) : ("done" as const),
          errorMessage: isErr ? t.content : undefined,
          result: isErr ? undefined : t.content,
        };
      }),
    });
    toolBuffer = [];
  };

  for (const msg of messages) {
    if (msg.role === "tool") {
      toolBuffer.push(msg);
      continue;
    }
    flushTools();
    if (msg.role === "assistant" && msg.plan) {
      result.push({ kind: "plan", spec: msg.plan });
    }
    if (msg.role === "user" || (msg.role === "assistant" && msg.content.trim())) {
      result.push({ kind: "message", message: msg });
    }
  }
  flushTools();
  return result;
}
